import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useApiKey } from '../contexts/ApiKeyContext';
import { HelpCircle } from 'lucide-react';

// Force Vercel deployment - updated dashboard component
const CustomerAnalyticsDashboard = ({ onShowHelp }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTool, setSelectedTool] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  
  // Get API key from context
  const { apiKey, error: apiKeyError } = useApiKey();
  
  // Available tools and use cases for dropdowns
  const availableTools = [
    'Agent Registration',
    'Agent Management',
    'Agent Prompt Editor',
    'Agent Self-Healing',
    'Agent Performance Tracking',
    'Agent Feedback Loop',
    'Agent Analytics'
  ];
  
  const availableUseCases = [
    'Agent Development',
    'Agent Optimization',
    'Agent Monitoring',
    'Agent Training',
    'Agent Deployment',
    'Agent Evaluation',
    'General'
  ];

  useEffect(() => {
    const fetchValidApiKeys = async () => {
      try {
        const apiUrl = getApiUrl('/valid-api-keys');
        console.log('Fetching valid API keys from URL:', apiUrl);

        const response = await axios.get(apiUrl, { withCredentials: true });
        console.log('Valid API keys fetched successfully:', response.data.keys);
        setValidApiKeys(response.data.keys || []);
      } catch (err) {
        console.error('Error fetching valid API keys:', err);
        console.error('Error response data:', err.response?.data);
        if (err.response?.status === 404) {
          console.warn('The valid API keys endpoint returned 404. Skipping to user-specific validation.');
          const userApiUrl = getApiUrl('/user/api-keys');
          console.log('Fetching user-specific API keys from URL:', userApiUrl);
          try {
            const userResponse = await axios.get(userApiUrl, { withCredentials: true });
            console.log('User-specific API keys fetched successfully:', userResponse.data.keys);
            setValidApiKeys(userResponse.data.keys || []);
          } catch (userErr) {
            console.error('Error fetching user-specific API keys:', userErr);
            console.error('Error response data:', userErr.response?.data);
            setError('Failed to fetch API keys. Please contact support.');
          }
        } else {
          console.error('Unexpected error while fetching valid API keys:', err);
          setError('An unexpected error occurred while fetching API keys.');
        }
      }
    };

    if (!apiKey) {
      console.warn('API key is not available. Attempting to fetch from sessionStorage.');
      const storedApiKey = sessionStorage.getItem('apiKey');
      if (storedApiKey) {
        console.log('API key found in sessionStorage:', storedApiKey);
        setValidApiKeys([storedApiKey]);
      } else {
        console.error('No API key found in sessionStorage. Cannot proceed.');
        setError('API key is missing. Please log in again.');
      }
    } else {
      fetchValidApiKeys();
    }
  }, [apiKey]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedTool) params.append('tool', selectedTool);
      if (selectedUseCase) params.append('use_case', selectedUseCase);

      const apiUrl = getApiUrl('/feedback/customer/analytics');
      console.log('Fetching analytics from URL:', apiUrl);
      console.log('Request headers:', { 'x-api-key': apiKey });

      if (!apiKey) {
        throw new Error('API key is missing. Cannot fetch analytics.');
      }

      const response = await axios.get(apiUrl, {
        params,
        headers: { 'x-api-key': apiKey },
        withCredentials: true
      });

      console.log('Analytics data fetched successfully:', response.data);
      setAnalyticsData(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      console.error('Error response data:', err.response?.data);
      if (err.response?.status === 401) {
        console.warn('Unauthorized request. Please check the API key.');
      }
      setError(err.response?.data?.error || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleApplyFilters = () => {
    fetchAnalytics();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedTool('');
    setSelectedUseCase('');
    fetchAnalytics();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };



  // Simple chart components
  const LineChart = ({ data, title }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <p className="text-gray-500">No data available for {title}</p>
        </div>
      );
    }

    const maxScore = Math.max(...data.map(d => d.average_score));
    const minScore = Math.min(...data.map(d => d.average_score));

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="space-y-2">
          {data.map((item, index) => {
            const score = item.average_score;
            const percentage = ((score - minScore) / (maxScore - minScore)) * 100;
            return (
              <div key={index} className="flex items-center space-x-4">
                <div className="w-20 text-sm text-gray-600">
                  {formatDate(item.date)}
                </div>
                <div className="flex-1">
                  <div className="bg-gray-200 rounded-full h-4">
                    <div 
                      className="bg-blue-500 h-4 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-16 text-sm font-medium text-gray-900">
                  {score.toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const PieChart = ({ data, title }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <p className="text-gray-500">No data available for {title}</p>
        </div>
      );
    }

    const total = data.reduce((sum, item) => sum + item.count, 0);
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500', 'bg-pink-500'];

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${colors[index % colors.length]}`}></div>
                <div className="flex-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">{item.tool || item.issue}</span>
                    <span className="font-medium text-gray-900">{item.count}</span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className={`${colors[index % colors.length]} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-12 text-xs text-gray-500 text-right">
                  {percentage}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const [validApiKeys, setValidApiKeys] = useState([]);

  useEffect(() => {
    const fetchValidApiKeys = async () => {
      try {
        const apiUrl = getApiUrl('/valid-api-keys');
        console.log('Fetching valid API keys from URL:', apiUrl);

        const response = await axios.get(apiUrl, { withCredentials: true });
        console.log('Valid API keys fetched successfully:', response.data.keys);
        setValidApiKeys(response.data.keys || []);
      } catch (err) {
        console.error('Error fetching valid API keys:', err);
        console.error('Error response data:', err.response?.data);
        if (err.response?.status === 404) {
          console.warn('The valid API keys endpoint returned 404. Skipping to user-specific validation.');
          const userApiUrl = getApiUrl('/user/api-keys');
          console.log('Fetching user-specific API keys from URL:', userApiUrl);
          try {
            const userResponse = await axios.get(userApiUrl, { withCredentials: true });
            console.log('User-specific API keys fetched successfully:', userResponse.data.keys);
            setValidApiKeys(userResponse.data.keys || []);
          } catch (userErr) {
            console.error('Error fetching user-specific API keys:', userErr);
            console.error('Error response data:', userErr.response?.data);
            setError('Failed to fetch API keys. Please contact support.');
          }
        } else {
          console.error('Unexpected error while fetching valid API keys:', err);
          setError('An unexpected error occurred while fetching API keys.');
        }
      }
    };

    if (!apiKey) {
      console.warn('API key is not available. Attempting to fetch from sessionStorage.');
      const storedApiKey = sessionStorage.getItem('apiKey');
      if (storedApiKey) {
        console.log('API key found in sessionStorage:', storedApiKey);
        setValidApiKeys([storedApiKey]);
      } else {
        console.error('No API key found in sessionStorage. Cannot proceed.');
        setError('API key is missing. Please log in again.');
      }
    } else {
      fetchValidApiKeys();
    }
  }, [apiKey]);

  if (!apiKey || !validApiKeys.includes(apiKey)) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <h1 className="text-3xl font-bold text-white mb-6">Customer Analytics Dashboard</h1>
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6">
            <h3 className="font-semibold text-red-300 mb-2">API Key Required</h3>
            <p className="text-red-300">
              {apiKeyError || 'API key is required to access this dashboard. Please contact your admin.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !analyticsData) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B61FF]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Customer Analytics Dashboard</h1>
            <p className="text-gray-400 mt-2">Track customer usage patterns and agent interactions across your customer base.</p>
          </div>
          {onShowHelp && (
            <button
              onClick={onShowHelp}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Help</span>
            </button>
          )}
        </div>

        {/* How This Works Section */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-600">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-blue-400 mb-3">📊 How Customer Analytics Works</h4>
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">1. Data Collection</span>
                    <p className="text-xs text-gray-400">Automatically collects feedback and usage data from all customer interactions</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">2. Pattern Analysis</span>
                    <p className="text-xs text-gray-400">Identifies usage trends, popular features, and customer behavior patterns</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">3. Performance Tracking</span>
                    <p className="text-xs text-gray-400">Monitors quality scores and identifies areas for improvement</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">4. Business Insights</span>
                    <p className="text-xs text-gray-400">Provides actionable insights to optimize your agent offerings</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-green-400 mb-3">🎯 Why This Matters</h4>
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">Product Decisions</span>
                    <p className="text-xs text-gray-400">Understand which features customers value most</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">Resource Allocation</span>
                    <p className="text-xs text-gray-400">Focus development efforts on high-impact improvements</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">Customer Success</span>
                    <p className="text-xs text-gray-400">Identify and address customer pain points proactively</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium">Growth Strategy</span>
                    <p className="text-xs text-gray-400">Data-driven decisions for scaling your AI agent platform</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tool
              </label>
              <select
                value={selectedTool}
                onChange={(e) => setSelectedTool(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              >
                <option value="">All Tools</option>
                {availableTools.map(tool => (
                  <option key={tool} value={tool}>{tool}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Use Case
              </label>
              <select
                value={selectedUseCase}
                onChange={(e) => setSelectedUseCase(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-xl bg-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF]"
              >
                <option value="">All Use Cases</option>
                {availableUseCases.map(useCase => (
                  <option key={useCase} value={useCase}>{useCase}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex space-x-4 mt-4">
            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="px-4 py-2 bg-[#7B61FF] text-white font-medium rounded-xl hover:bg-[#6B51EF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B61FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-700 text-white font-medium rounded-xl hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-300"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {analyticsData && (
          <>
            {/* Summary Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-gray-800 rounded-xl shadow p-6 border-l-4 border-[#7B61FF]" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.2)' }}>
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-400">Average Score</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.average_score?.toFixed(1) || 'N/A'}
                    </p>
                  </div>
                  <div className="text-[#7B61FF]">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl shadow p-6 border-l-4 border-[#00CFFF]" style={{ boxShadow: '0 0 15px rgba(0, 207, 255, 0.2)' }}>
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-400">Total Logs</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.total_logs || 0}
                    </p>
                  </div>
                  <div className="text-[#00CFFF]">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl shadow p-6 border-l-4 border-blue-500" style={{ boxShadow: '0 0 15px rgba(59, 130, 246, 0.2)' }}>
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-400">Average Score</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.average_score?.toFixed(1) || 'N/A'}
                    </p>
                  </div>
                  <div className="text-blue-500">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl shadow p-6 border-l-4 border-purple-500" style={{ boxShadow: '0 0 15px rgba(168, 85, 247, 0.2)' }}>
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-400">Success Rate</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.average_score ? 
                        (analyticsData.average_score >= 70 ? 'High' : 
                         analyticsData.average_score >= 50 ? 'Medium' : 'Low') : 'N/A'}
                    </p>
                  </div>
                  <div className="text-purple-500">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LineChart 
                data={analyticsData.score_over_time} 
                title="Score Over Time" 
              />
              
              <PieChart 
                data={analyticsData.tool_usage} 
                title="Tool Usage Breakdown" 
              />
              
              <PieChart 
                data={analyticsData.issue_breakdown} 
                title="Issue Breakdown" 
              />
              
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {analyticsData.score_over_time?.slice(-5).reverse().map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(item.date)}
                        </p>
                        <p className="text-xs text-gray-500">Average Score</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">
                          {item.average_score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerAnalyticsDashboard;